import {
  Engine,
  Scene,
  ArcRotateCamera,
  HemisphericLight,
  DirectionalLight,
  Vector3,
  Color3,
  Color4,
  MeshBuilder,
  StandardMaterial,
  Mesh,
  GlowLayer,
} from "@babylonjs/core";

/**
 * Représentation 3D du globe — version simplifiée et robuste.
 *
 * Choix de simplification (suite à des hex invisibles) :
 * - Plus de prismes hexagonaux orientés : on revient à des SPHÈRES.
 * - Tailles plus généreuses pour qu'elles soient visibles à coup sûr.
 * - Logs console pour vérifier que le code tourne.
 */

export interface PlayerVisualData {
  id: string;
  name: string;
  seatIndex: number;
  status: string;
  territorySize: number;
  maxDensity: number;
  hexes: Array<{ density: number }>;
}

export interface ChargingVisualData {
  id: string;
  ownerId: string;
  cardName: string;
  turnsRemaining: number;
}

const PLAYER_COLORS: Color3[] = [
  Color3.FromHexString("#d97b7b"),
  Color3.FromHexString("#7ba89b"),
  Color3.FromHexString("#9b7bc4"),
  Color3.FromHexString("#d4b96f"),
  Color3.FromHexString("#6b8fb5"),
  Color3.FromHexString("#c490a7"),
  Color3.FromHexString("#9bc490"),
  Color3.FromHexString("#c4a8e6"),
];

const GLOBE_RADIUS = 3;
const HOME_DIAMETER = 0.45;
const HEX_DIAMETER = 0.25;
const HEX_ORBIT_DIST = 0.5;
const CHARGE_PILLAR_HEIGHT = 0.7;
const CHARGE_PILLAR_RADIUS = 0.08;

interface PlayerNode {
  home: Mesh;
  hexes: Mesh[];
  color: Color3;
  status: string;
  charges: Map<string, Mesh>;
}

export class GlobeScene {
  private engine: Engine;
  private scene: Scene;
  private globe!: Mesh;
  private playerNodes = new Map<string, PlayerNode>();
  private elapsedTime: number = 0;
  private maxPlayers: number = 8;

  constructor(canvas: HTMLCanvasElement) {
    console.log("[Globe] Init avec canvas", canvas.width, "x", canvas.height, "client:", canvas.clientWidth, "x", canvas.clientHeight);

    this.engine = new Engine(canvas, true, { antialias: true });
    this.scene = new Scene(this.engine);
    this.scene.clearColor = new Color4(0.984, 0.965, 0.933, 1);

    this.setupCamera(canvas);
    this.setupLights();
    this.setupGlobe();
    this.setupGlow();
    this.setupAnimationLoop();

    this.engine.runRenderLoop(() => this.scene.render());
    window.addEventListener("resize", () => this.engine.resize());

    console.log("[Globe] Scene prête.");
  }

  private setupCamera(canvas: HTMLCanvasElement) {
    const camera = new ArcRotateCamera(
      "camera",
      -Math.PI / 2,
      Math.PI / 2.3,
      9,
      Vector3.Zero(),
      this.scene
    );
    camera.attachControl(canvas, true);
    camera.lowerRadiusLimit = 5;
    camera.upperRadiusLimit = 18;
    camera.wheelDeltaPercentage = 0.01;
  }

  private setupLights() {
    const hemi = new HemisphericLight("hemi", new Vector3(0, 1, 0), this.scene);
    hemi.intensity = 0.8;
    hemi.diffuse = new Color3(1, 0.96, 0.92);
    hemi.groundColor = new Color3(0.7, 0.65, 0.6);

    const dir = new DirectionalLight("dir", new Vector3(-1, -2, -1), this.scene);
    dir.intensity = 0.35;
  }

  private setupGlobe() {
    this.globe = MeshBuilder.CreateSphere("globe", { diameter: GLOBE_RADIUS * 2, segments: 48 }, this.scene);
    const mat = new StandardMaterial("globeMat", this.scene);
    mat.diffuseColor = Color3.FromHexString("#c8dde9");
    mat.specularColor = new Color3(0.2, 0.2, 0.25);
    mat.emissiveColor = Color3.FromHexString("#15202c");
    this.globe.material = mat;
  }

  private setupGlow() {
    const glow = new GlowLayer("glow", this.scene);
    glow.intensity = 0.6;
  }

  private setupAnimationLoop() {
    this.scene.onBeforeRenderObservable.add(() => {
      const dt = this.engine.getDeltaTime() / 1000;
      this.elapsedTime += dt;

      // Rotation idle subtile
      if (this.globe) this.globe.rotation.y += dt * 0.02;

      const pulse = (Math.sin(this.elapsedTime * 4) + 1) / 2;

      for (const node of this.playerNodes.values()) {
        // Pulsation Exposé sur le home
        const mat = node.home.material as StandardMaterial;
        if (node.status === "expose") {
          mat.emissiveColor = new Color3(0.4 + pulse * 0.6, 0.1, 0.1);
        }

        // Animation des piliers de chargement
        for (const pillar of node.charges.values()) {
          pillar.rotation.y += dt * 2.5;
          const scale = 1 + Math.sin(this.elapsedTime * 3) * 0.08;
          pillar.scaling.y = scale;
          const pmat = pillar.material as StandardMaterial;
          pmat.emissiveColor = node.color.scale(0.6 + pulse * 0.4);
        }
      }
    });
  }

  private seatToPosition(seatIndex: number, offset: number = 0): Vector3 {
    const slots = this.maxPlayers;
    const angle = (seatIndex / slots) * Math.PI * 2;
    const tilt = Math.PI / 2 - 0.3;
    const radius = GLOBE_RADIUS + offset;
    const x = radius * Math.sin(tilt) * Math.cos(angle);
    const y = radius * Math.cos(tilt);
    const z = radius * Math.sin(tilt) * Math.sin(angle);
    return new Vector3(x, y, z);
  }

  resize() {
    this.engine.resize();
  }

  update(players: PlayerVisualData[], chargingCards: ChargingVisualData[] = []) {
    console.log(`[Globe] update() — ${players.length} joueurs, ${chargingCards.length} chargements`);

    const visibleIds = new Set(players.map((p) => p.id));

    // Supprime les joueurs qui ne sont plus là
    for (const [id, node] of this.playerNodes.entries()) {
      if (!visibleIds.has(id)) {
        node.home.dispose();
        for (const h of node.hexes) h.dispose();
        for (const pillar of node.charges.values()) pillar.dispose();
        this.playerNodes.delete(id);
      }
    }

    for (const p of players) {
      let node = this.playerNodes.get(p.id);
      const color = PLAYER_COLORS[p.seatIndex % PLAYER_COLORS.length]!;

      if (!node) {
        const home = MeshBuilder.CreateSphere(
          `home-${p.id}`,
          { diameter: HOME_DIAMETER, segments: 16 },
          this.scene
        );
        const homeMat = new StandardMaterial(`homeMat-${p.id}`, this.scene);
        homeMat.diffuseColor = color;
        homeMat.emissiveColor = color.scale(0.5);
        home.material = homeMat;

        node = { home, hexes: [], color, status: p.status, charges: new Map() };
        this.playerNodes.set(p.id, node);
        console.log(`[Globe] Création home pour ${p.name} (seat ${p.seatIndex})`);
      }

      const homePos = this.seatToPosition(p.seatIndex, 0.15);
      node.home.position = homePos;
      node.status = p.status;

      // Couleur du home selon le status
      const homeMat = node.home.material as StandardMaterial;
      if (p.status === "vassal") {
        homeMat.diffuseColor = color.scale(0.5);
        homeMat.emissiveColor = new Color3(0.1, 0.1, 0.1);
      } else if (p.status === "eliminated") {
        homeMat.diffuseColor = new Color3(0.25, 0.25, 0.25);
        homeMat.emissiveColor = new Color3(0, 0, 0);
      } else if (p.status !== "expose") {
        homeMat.diffuseColor = color;
        homeMat.emissiveColor = color.scale(0.5);
      }

      this.syncHexes(p, node, homePos, color);
      this.syncCharges(p, node, homePos, chargingCards);
    }
  }

  private syncHexes(
    player: PlayerVisualData,
    node: PlayerNode,
    homePos: Vector3,
    color: Color3
  ) {
    const needed = player.hexes.length;

    // Supprime les hex en trop
    while (node.hexes.length > needed) {
      const mesh = node.hexes.pop();
      if (mesh) mesh.dispose();
    }

    // Crée les hex manquants
    while (node.hexes.length < needed) {
      const i = node.hexes.length;
      const mesh = MeshBuilder.CreateSphere(
        `hex-${player.id}-${i}-${Date.now()}`,
        { diameter: HEX_DIAMETER, segments: 12 },
        this.scene
      );
      const mat = new StandardMaterial(`hexMat-${player.id}-${i}`, this.scene);
      mat.diffuseColor = color;
      mat.emissiveColor = color.scale(0.4);
      mesh.material = mat;
      node.hexes.push(mesh);
    }

    // Place les hex en cluster autour du home
    const homeNorm = homePos.normalize();
    const up = Math.abs(homeNorm.y) < 0.95 ? new Vector3(0, 1, 0) : new Vector3(1, 0, 0);
    const tangent = Vector3.Cross(up, homeNorm).normalize();
    const bitangent = Vector3.Cross(homeNorm, tangent).normalize();

    for (let i = 0; i < node.hexes.length; i++) {
      const hex = node.hexes[i]!;
      const density = Math.max(1, Math.min(5, player.hexes[i]?.density ?? 1));

      // Spirale autour du home
      const angle = i * 1.2;
      const dist = HEX_ORBIT_DIST + Math.sqrt(i) * 0.18;
      const offset = tangent.scale(Math.cos(angle) * dist).add(bitangent.scale(Math.sin(angle) * dist));
      // Position sur la sphère + petite élévation
      const pos = homePos.add(offset).normalize().scale(GLOBE_RADIUS + 0.18);
      hex.position = pos;

      // Couleur et émissivité selon densité
      const mat = hex.material as StandardMaterial;
      const densityScale = density / 5;
      mat.diffuseColor = color.scale(0.5 + densityScale * 0.5);
      mat.emissiveColor = color.scale(densityScale * 0.5);

      // Taille selon densité
      const scale = 0.8 + densityScale * 0.6;
      hex.scaling.setAll(scale);
    }
  }

  private syncCharges(
    player: PlayerVisualData,
    node: PlayerNode,
    homePos: Vector3,
    chargingCards: ChargingVisualData[]
  ) {
    const myCharges = chargingCards.filter((c) => c.ownerId === player.id);
    const myChargeIds = new Set(myCharges.map((c) => c.id));

    for (const [id, pillar] of node.charges.entries()) {
      if (!myChargeIds.has(id)) {
        pillar.dispose();
        node.charges.delete(id);
      }
    }

    const homeNorm = homePos.normalize();
    for (let i = 0; i < myCharges.length; i++) {
      const c = myCharges[i]!;
      let pillar = node.charges.get(c.id);
      if (!pillar) {
        pillar = MeshBuilder.CreateCylinder(
          `charge-${c.id}`,
          {
            height: CHARGE_PILLAR_HEIGHT,
            diameterTop: CHARGE_PILLAR_RADIUS * 1.5,
            diameterBottom: CHARGE_PILLAR_RADIUS,
          },
          this.scene
        );
        const mat = new StandardMaterial(`chargeMat-${c.id}`, this.scene);
        mat.diffuseColor = node.color;
        mat.emissiveColor = node.color.scale(0.6);
        pillar.material = mat;
        node.charges.set(c.id, pillar);
      }
      const offset = i * 0.18;
      const pos = homePos.add(homeNorm.scale(CHARGE_PILLAR_HEIGHT / 2 + 0.2 + offset));
      pillar.position = pos;
      pillar.lookAt(homePos.scale(2));
      pillar.rotation.x += Math.PI / 2;
    }
  }

  dispose() {
    this.engine.dispose();
  }
}
